import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Typography, Spin, Table, Tag, Button, Descriptions, Divider, Space } from 'antd';
import {
  ArrowLeftOutlined, ShopOutlined, DollarOutlined, TeamOutlined,
  ShoppingCartOutlined, HeartOutlined, WarningOutlined
} from '@ant-design/icons';
import api from '../../api/axios';

const { Title, Text } = Typography;
const fmt = (v) => Number(v || 0).toLocaleString('uz-UZ');
const STATUS_COLOR = { pending: 'orange', confirmed: 'blue', in_progress: 'cyan', completed: 'green', cancelled: 'red' };

export default function TaxVendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admingov/tax/vendor/${id}`)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  if (!data) return <div style={{ textAlign: 'center', padding: 80 }}>Ma'lumot topilmadi</div>;

  const { business: biz, services, orders, payments, employees, servicePay, weddingMentions, monthlyTrend } = data;

  const totalOrderAmount = orders.reduce((s, o) => s + +(o.summa || 0), 0);
  const totalPayments = payments.reduce((s, p) => s + +(p.summa || 0), 0);
  const totalSalary = employees.reduce((s, e) => s + +(e.salary || 0), 0);
  const totalPlatformPay = servicePay.reduce((s, p) => s + +(p.amount || 0), 0);

  return (
    <div>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admingov/tax')} style={{ marginBottom: 16 }}>
        Ortga
      </Button>

      <Title level={3}><ShopOutlined /> {biz.name} — Soliq Tahlili</Title>

      <Card style={{ borderRadius: 12, marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
          <Descriptions.Item label="Biznes nomi">{biz.name}</Descriptions.Item>
          <Descriptions.Item label="STIR">{biz.stir || <Tag color="red">Yo'q</Tag>}</Descriptions.Item>
          <Descriptions.Item label="Egasi">{biz.owner_name}</Descriptions.Item>
          <Descriptions.Item label="Telefon">{biz.phone}</Descriptions.Item>
          <Descriptions.Item label="Viloyat">{biz.region}</Descriptions.Item>
          <Descriptions.Item label="Tuman">{biz.district}</Descriptions.Item>
          <Descriptions.Item label="Manzil">{biz.address}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 12 }}>
            <Statistic title="Buyurtmalar" value={orders.length} prefix={<ShoppingCartOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 12 }}>
            <Statistic title="Jami daromad" value={fmt(totalOrderAmount)} suffix="so'm" />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 12 }}>
            <Statistic title="To'lovlar" value={fmt(totalPayments)} suffix="so'm" valueStyle={{ color: totalPayments < totalOrderAmount * 0.5 ? '#f5222d' : '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 12 }}>
            <Statistic title="Xodimlar" value={employees.length} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 12 }}>
            <Statistic title="Maosh fondi" value={fmt(totalSalary)} suffix="so'm" />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 12 }}>
            <Statistic title="Platform to'lov" value={fmt(totalPlatformPay)} suffix="so'm" />
          </Card>
        </Col>
      </Row>

      {/* To'lov / daromad nisbati */}
      {totalOrderAmount > 0 && (
        <Card style={{ borderRadius: 12, marginBottom: 16, background: totalPayments / totalOrderAmount < 0.3 ? '#fff1f0' : '#f6ffed', border: '1px solid ' + (totalPayments / totalOrderAmount < 0.3 ? '#ffa39e' : '#b7eb8f') }}>
          <Space>
            <WarningOutlined style={{ fontSize: 20, color: totalPayments / totalOrderAmount < 0.3 ? '#f5222d' : '#52c41a' }} />
            <Text strong>
              To'lov / Daromad nisbati: {((totalPayments / totalOrderAmount) * 100).toFixed(1)}%
              {totalPayments / totalOrderAmount < 0.3 && ' — OGOHLANTIRISH: Juda past nisbat!'}
            </Text>
          </Space>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        {/* Xizmatlar */}
        <Col xs={24} lg={12}>
          <Card title="Xizmatlar" style={{ borderRadius: 12, marginBottom: 16 }}>
            <Table dataSource={services} rowKey="id" pagination={false} size="small"
              columns={[
                { title: 'Nomi', dataIndex: 'name' },
                { title: 'Turi', dataIndex: 'type_name', render: v => <Tag color="blue">{v}</Tag> },
              ]}
            />
          </Card>
        </Col>

        {/* Xodimlar */}
        <Col xs={24} lg={12}>
          <Card title="Xodimlar" style={{ borderRadius: 12, marginBottom: 16 }}>
            <Table dataSource={employees} rowKey="id" pagination={false} size="small"
              columns={[
                { title: 'ID', dataIndex: 'users_id', width: 50 },
                { title: 'Maosh', dataIndex: 'salary', render: v => fmt(v) + " so'm" },
                { title: 'Turi', dataIndex: 'salary_type' },
                { title: 'JSHSHIR', dataIndex: 'jshshir', render: v => v || <Tag color="red">Yo'q</Tag> },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Buyurtmalar */}
      <Card title="Oxirgi buyurtmalar" style={{ borderRadius: 12, marginBottom: 16 }}>
        <Table dataSource={orders.slice(0, 20)} rowKey="id" pagination={false} size="small" scroll={{ x: 600 }}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 50 },
            { title: 'Summa', dataIndex: 'summa', render: v => fmt(v) + " so'm" },
            { title: 'Status', dataIndex: 'status', render: s => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
            { title: 'Sana', render: (_, r) => r.day ? `${r.day}.${r.month}.${r.year}` : '-' },
          ]}
        />
      </Card>

      {/* To'lovlar */}
      <Card title="To'lovlar tarixi" style={{ borderRadius: 12, marginBottom: 16 }}>
        <Table dataSource={payments} rowKey="id" pagination={false} size="small"
          columns={[
            { title: 'Buyurtma ID', dataIndex: 'orders_id' },
            { title: 'Summa', dataIndex: 'summa', render: v => fmt(v) + " so'm" },
            { title: 'Turi', dataIndex: 'paytype' },
            { title: 'Sana', dataIndex: 'date', render: v => v ? new Date(v).toLocaleDateString('uz-UZ') : '-' },
          ]}
        />
      </Card>

      {/* To'y rejalashtirish aloqalari */}
      {weddingMentions.length > 0 && (
        <Card title={<><HeartOutlined style={{ color: '#eb2f96' }} /> To'y rejalashtirish moduli aloqalari</>} style={{ borderRadius: 12, marginBottom: 16 }}>
          <Table dataSource={weddingMentions} rowKey="id" pagination={false} size="small" scroll={{ x: 600 }}
            columns={[
              { title: 'Reja', dataIndex: 'plan_title' },
              { title: "To'y sanasi", dataIndex: 'wedding_date' },
              { title: 'Vendor nomi', dataIndex: 'vendor_name' },
              { title: 'Telefon', dataIndex: 'vendor_phone' },
              { title: 'Summa', dataIndex: 'amount', render: v => fmt(v) + " so'm" },
              { title: "To'langan", dataIndex: 'paid_amount', render: v => fmt(v) + " so'm" },
              { title: 'Status', dataIndex: 'payment_status', render: s => <Tag color={s === 'paid' ? 'green' : s === 'partial' ? 'orange' : 'red'}>{s}</Tag> },
            ]}
          />
        </Card>
      )}

      {/* Oylik trend */}
      <Card title="Oylik buyurtma trendi (12 oy)" style={{ borderRadius: 12 }}>
        <Table dataSource={monthlyTrend} rowKey="month" pagination={false} size="small"
          columns={[
            { title: 'Oy', dataIndex: 'month' },
            { title: 'Buyurtmalar', dataIndex: 'count' },
            { title: 'Daromad', dataIndex: 'revenue', render: v => fmt(v) + " so'm" },
          ]}
        />
      </Card>
    </div>
  );
}
